import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { buildFormData, buildRequestData } from '../helpers';
import { useAutomationService, useAutomationsService } from '../services';
import { Automation, AutomationFormData } from '../types';
import { useAutomationFormDialogController } from './AutomationFormDialogController';
import { useAutomationOptions } from './useAutomationOptions';

export const useAutomationsViewController = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;

  const {
    loading: automationsLoading,
    filter: automationsFilter,
    list: automationsList,
    getAutomations,
    changeAutomationsFilter,
    resetAutomations,
  } = useAutomationsService();
  const { deleteAutomation, updateAutomation } = useAutomationService();

  const automationFormDialogController = useAutomationFormDialogController();
  const { options } = useAutomationOptions();

  const reloadAutomations = useCallback(async () => {
    await getAutomations(currentCompanyId);
  }, [getAutomations, currentCompanyId]);

  const reloadAutomationsDebounce = useMemo(
    () => debounce(reloadAutomations, 500),
    [reloadAutomations],
  );

  const changeAutomationsSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changeAutomationsFilter({ query, page: 0, offset: 0 });
      await reloadAutomationsDebounce();
    },
    [changeAutomationsFilter, reloadAutomationsDebounce],
  );

  const changeAutomationsSorting = useCallback(
    async (s: GridSortModel) => {
      if (!s || s.length === 0) return;
      changeAutomationsFilter({ sort: `${s[0].field}_${s[0].sort}` });
      await reloadAutomations();
    },
    [changeAutomationsFilter, reloadAutomations],
  );

  const changeAutomationsPagination = useCallback(
    async (pagination: GridPaginationModel) => {
      changeAutomationsFilter({
        page: pagination.page,
        limit: pagination.pageSize,
        offset: pagination.page * pagination.pageSize,
      });
      await reloadAutomations();
    },
    [changeAutomationsFilter, reloadAutomations],
  );

  const removeAutomation = useCallback(
    async (automationId: string) => {
      await deleteAutomation(currentCompanyId, automationId);
      await reloadAutomations();
    },
    [deleteAutomation, reloadAutomations, currentCompanyId],
  );

  // Flip active <-> inactive by reusing the full update endpoint: rebuild the
  // save payload from the row and override only its status.
  const toggleAutomationStatus = useCallback(
    async (automation: Automation) => {
      const payload = buildRequestData(buildFormData(automation));
      payload.status = automation.status === 'active' ? 'inactive' : 'active';

      await updateAutomation(currentCompanyId, automation.id, payload);
      await reloadAutomations();
    },
    [updateAutomation, reloadAutomations, currentCompanyId],
  );

  useEffect(() => {
    reloadAutomations();

    return () => {
      resetAutomations();
    };
  }, []);

  return {
    isPageLoading: automationsLoading.getList,
    automationsFilter: automationsFilter as RootState['automations']['filter'],
    automationsList,
    options,
    changeAutomationsSearchFilter,
    changeAutomationsSorting,
    changeAutomationsPagination,
    removeAutomation,
    toggleAutomationStatus,
    automationFormDialogController: {
      ...automationFormDialogController,
      submit: (full: AutomationFormData) =>
        automationFormDialogController.submit(full, () => reloadAutomations()),
    },
  };
};
